import {
    AirTerminalInstallation,
    BaseComponent,
    BlueprintLocation,
    Building,
    ColorModel,
    DoorType,
    ElectricalInstallation,
    HazardousModel,
    LevelModel,
    PhotoLocation,
    PhotoModel,
    RadiatorInstallation,
    RiskConstruction,
    RoomModel,
    SurfaceType,
    ValueStatus,
    ValueWithMetadata,
    WindowType
} from "./protocols";
import {
    BaseType,
    BuildComponentType,
    CardinalDirection,
    ElevatorType,
    Material,
    RoofShape,
    RoomType,
    UtilityCategory
} from "./Enums";
import {translate} from "./EnumTranslations";
import {AnyGeometryShape, computeShapeArea} from "./GeometryShapes";
import {getCustomTypeDisplayName, formatLevelNumber} from "../Helpers/utils";

export class BaseComponentImpl implements BaseComponent {
    key: string;
    type: BaseType;
    createdBy: string;
    createdWhen: string;
    comment: ValueWithMetadata<string>;
    photos?: { [key: string]: PhotoModel };
    specificType?: BuildComponentType;
    condition?: ValueWithMetadata<number>;
    buildingRisks?: ValueWithMetadata<RiskConstruction>;
    material?: ValueWithMetadata<Material[]>;
    color?: ValueWithMetadata<ColorModel>;
    areaShapes?: ValueWithMetadata<AnyGeometryShape[]>;
    cardinalDirection?: ValueWithMetadata<CardinalDirection>;
    depth?: ValueWithMetadata<number>;
    insulation?: ValueWithMetadata<number>;
    label?: string;
    hazardous?: ValueWithMetadata<HazardousModel>;
    canHaveWindows?: Boolean;
    canHaveDoors?: Boolean;
    canHaveLock?: Boolean;
    roofSlope?: ValueWithMetadata<number>;
    roofShape?: ValueWithMetadata<RoofShape>;
    parentRoomKeys?: ValueWithMetadata<string[]>;
    typeKey?: ValueWithMetadata<string>;
    roomKey?: string;
    utilityCategory?: UtilityCategory;
    elevatorType?: ValueWithMetadata<ElevatorType>;
    mapLocations?: { [key: string]: BlueprintLocation };
    blueprintLocations?: { [key: string]: BlueprintLocation };
    installYear?: ValueWithMetadata<number>;
    photoLocation?: PhotoLocation;

    parentSurface?: ValueWithMetadata<string>;
    parentSurface1?: ValueWithMetadata<string>;
    parentSurface2?: ValueWithMetadata<string>;

    constructor(data: any) {
        // Required fields
        this.key = data.key;
        this.type = data.type;
        this.createdBy = data.createdBy;
        this.createdWhen = data.createdWhen;
        this.comment = data.comment;

        // Optional fields - use Object.assign for cleaner assignment
        const optionalFields = [
            'photos', 'specificType', 'condition', 'buildingRisks', 'material',
            'color', 'areaShapes', 'cardinalDirection', 'depth', 'insulation',
            'label', 'hazardous', 'canHaveWindows', 'canHaveDoors',
            'canHaveLock',
            'roofSlope', 'roofShape', 'parentRoomKeys', 'typeKey', 'roomKey',
            'utilityCategory', 'elevatorType', 'mapLocations', 'blueprintLocations',
            'parentSurface', 'parentSurface1', 'parentSurface2', 'installYear',
            'photoLocation'
        ];

        for (const field of optionalFields) {
            if (data[field] !== undefined) {
                (this as any)[field] = data[field];
            }
        }
    }

    displayName(building: Building): string {
        let name = "";
        
        // First check if component has a label
        if (this.label) {
            name = this.label;
        } else if (this.typeKey?.value && building?.model?.structure?.types) {
            // Look up custom type and use its displayName() logic
            const types = building.model.structure.types;
            const typeKey = this.typeKey.value;
            
            let customType: SurfaceType | WindowType | DoorType | null = null;
            if (this.type === BaseType.Wall || this.type === BaseType.Ceiling || this.type === BaseType.Floor) {
                customType = types.surfaceTypes?.[typeKey];
            } else if (this.type === BaseType.Window) {
                customType = types.windowTypes?.[typeKey];
            } else if (this.type === BaseType.Door) {
                customType = types.doorTypes?.[typeKey];
            }
            
            // Use custom type's displayName (label, or built from material/color)
            name = getCustomTypeDisplayName(customType) || typeKey;
        } else if (this.roofShape?.value) {
            name = translate(this.roofShape.value)
        } else if (this.specificType) {
            name = translate(this.specificType);
        } else {
            name = translate(this.type);
        }

        if (this.cardinalDirection?.value) {
            name = `${name} (${translate(this.cardinalDirection.value)})`;
        }

        return name;
    }

    /**
     * Calculates the total area of all shapes in areaShapes
     * For windows and doors, uses modular size from custom type if available
     * @param building The building object to look up custom types
     * @returns The sum of all shape areas or null if no areas are set
     */
    totalArea(building: Building): number | null {
        // For windows and doors, check if they have a custom type with modular size
        if ((this.type === BaseType.Window || this.type === BaseType.Door) && this.typeKey?.value && building.model.structure?.types) {
            const types = building.model.structure.types;
            const typeKey = this.typeKey.value;
            
            let customType: WindowType | DoorType | null = null;
            if (this.type === BaseType.Window) {
                customType = types.windowTypes?.[typeKey];
            } else if (this.type === BaseType.Door) {
                customType = types.doorTypes?.[typeKey];
            }
            
            // If custom type has modular size, use it (preferred for windows/doors)
            if (customType?.modularSize?.value) {
                const modularSize = customType.modularSize.value;
                // modularSize is in meters (SI units), so width * height = area in m²
                return modularSize.width * modularSize.height;
            }
        }
        
        // Default: use areaShapes
        if (!this.areaShapes || !this.areaShapes.value || this.areaShapes.value.length === 0) {
            return null;
        }

        let totalArea = 0;
        for (const shape of this.areaShapes.value) {
            totalArea += computeShapeArea(shape);
        }

        return totalArea;
    }

    /**
     * Returns the most recent date across creation timestamp, all ValueWithMetadata fields, and attached photos, formatted as YYYY-MM-DD
     * @returns Latest date string
     */
    lastUpdated(): string {
        const dates: string[] = [];

        // Component's creation timestamp is mandatory
        dates.push(this.createdWhen);

        // Collect dates from all ValueWithMetadata fields
        const fields: Array<ValueWithMetadata<any> | undefined> = [
            this.comment,
            this.condition,
            this.buildingRisks,
            this.material,
            this.color,
            this.areaShapes,
            this.cardinalDirection,
            this.depth,
            this.insulation,
            this.roofSlope,
            this.roofShape,
            this.parentRoomKeys,
            this.typeKey
        ];

        for (const field of fields) {
            if (field?.updatedWhen) {
                dates.push(field.updatedWhen);
            }
        }

        // Include upload dates from photos
        if (this.photos) {
            for (const photo of Object.values(this.photos)) {
                if (photo.date) {
                    dates.push(photo.date);
                }
            }
        }

        // Determine the latest date-time
        const latest = dates
            .map(dateStr => new Date(dateStr))
            .reduce((prev, curr) => prev > curr ? prev : curr);

        // Format as YYYY-MM-DD
        const year = latest.getFullYear();
        const month = String(latest.getMonth() + 1).padStart(2, '0');
        const day = String(latest.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}

export class RoomImpl implements RoomModel {
    key: string = "";
    number?: string;
    label?: string;
    levelKey?: string;
    location?: BlueprintLocation;
    blueprintLocations?: { [key: string]: BlueprintLocation };
    type?: RoomType;
    ceilingHeight: ValueWithMetadata<number>;
    photos?: { [key: string]: PhotoModel };
    electricalInstallation?: ValueWithMetadata<ElectricalInstallation>;
    airTerminalInstallation?: ValueWithMetadata<AirTerminalInstallation>;
    radiatorInstallation?: ValueWithMetadata<RadiatorInstallation>;

    constructor(data: RoomModel) {
        this.ceilingHeight = new ValueWithMetadataImpl<number>();
        Object.assign(this, data);

        // Migration logic: if old location exists but blueprintLocations doesn't,
        // create blueprintLocations with a default blueprint
        if (this.location && !this.blueprintLocations) {
            this.blueprintLocations = {
                'default': this.location
            };
        }
    }

    displayName(): string {
        if (this.label && this.number) {
            return `Rum ${this.number} - ${this.label}`
        }
        if (this.number) {
            return `Rum ${this.number}`
        }
        if (this.label) {
            return this.label
        }
        if (this.type) {
            return translate(this.type)
        }
        return "Odefinierat rum"
    }

    /**
     * Get the blueprint location for a specific blueprint key
     */
    getBlueprintLocation(blueprintKey: string): BlueprintLocation | undefined {
        return this.blueprintLocations?.[blueprintKey];
    }

    /**
     * Set the blueprint location for a specific blueprint key
     */
    setBlueprintLocation(blueprintKey: string, location: BlueprintLocation): void {
        if (!this.blueprintLocations) {
            this.blueprintLocations = {};
        }
        this.blueprintLocations[blueprintKey] = location;
    }

    /**
     * Remove the blueprint location for a specific blueprint key
     */
    removeBlueprintLocation(blueprintKey: string): void {
        if (this.blueprintLocations) {
            delete this.blueprintLocations[blueprintKey];
        }
    }

    /**
     * Get all blueprint keys that this room has locations for
     */
    getAvailableBlueprintKeys(): string[] {
        return this.blueprintLocations ? Object.keys(this.blueprintLocations) : [];
    }

    /**
     * Check if room has a location for a specific blueprint
     */
    hasLocationForBlueprint(blueprintKey: string): boolean {
        return !!(this.blueprintLocations && this.blueprintLocations[blueprintKey]);
    }
}

export class LevelImpl implements LevelModel {
    key: string;
    name?: string;
    number: number;
    ceilingHeight?: number;
    type?: any;
    planImage?: PhotoModel;
    currentBlueprintKey?: string;
    blueprints?: { [key: string]: PhotoModel };
    roofPlan?: boolean; // true if this level is the roof plan (Takplan)

    constructor(data: LevelModel) {
        this.key = data.key;
        this.number = data.number;
        Object.assign(this, data);
    }

    displayName(): string {
        if (this.roofPlan === true) {
            return "Takplan";
        }
        return `Våning ${this.formattedNumber()}`;
    }

    formattedNumber(): string {
        return formatLevelNumber(this.number);
    }
}

export class ValueWithMetadataImpl<T> implements ValueWithMetadata<T> {
    readonly value: T | null;
    readonly updatedWhen: string | null;
    readonly updatedBy: string | null;
    readonly status: ValueStatus;

    constructor(value: T | null = null, updatedWhen: string | null = null,
                updatedBy: string | null = null, status: ValueStatus = ValueStatus.notSet) {
        this.value = value;
        this.updatedWhen = updatedWhen;
        this.updatedBy = updatedBy;
        this.status = status;
    }
}